<script>
import IaPainelLateral from '@modules/global/components/ia-painel-lateral/ia-painel-lateral.vue'
import { abrirServicoCitado } from '@modules/solicitacoes/utils/abrirServicoCitado'
import ViewChat from '@modules/aawz-intelligence-v2/views/chat/view-chat.vue'
import Conversations from '@modules/aawz-intelligence-v2/components/conversations/conversations.vue'
import { historicoConversas } from '@modules/aawz-intelligence-v2/mixins/historico-conversas'
import {
  getPersona,
  PERSONA_CONSULTOR_ESTRATEGICO,
  ORIGEM_SOLICITACOES,
} from '@modules/aawz-intelligence-v2/constants/Personas'
import { AGENTE_CONSULTOR } from '@modules/aawz-intelligence-v2/constants/Agentes'

const PERSONA = getPersona(PERSONA_CONSULTOR_ESTRATEGICO)
// A aba recolhida tem 40px: o rótulo é vertical e precisa ser curto.
const ROTULO_ABA = 'Consultor'

/**
 * O Consultor Estratégico como painel à direita do AAWZ Hub.
 *
 * Existe porque o consultor LÊ a jornada do escritório — e, na rota dedicada
 * (`/solicitacoes/consultor`), a jornada sai da tela justamente na hora de
 * conversar sobre ela. Aqui ciclo, trilha e catálogo continuam visíveis ao lado
 * da resposta.
 *
 * Não substitui aquela rota: ela segue alcançável pela aba do nav. Este é um
 * caminho A MAIS, e é isso que mantém a mudança contida.
 *
 * Histórico e nova conversa vivem no cabeçalho, como no popup: em modo `widget`
 * a sidebar de conversas não cabe, e sem eles o gestor fica preso na conversa
 * aberta, sem como voltar às anteriores.
 */
export default {
  name: 'ConsultorPainel',
  components: { IaPainelLateral, ViewChat, Conversations },
  // Abrir / criar / renomear / apagar conversa: o mesmo bloco que o popup usa.
  // Este painel não implementa `adotarAgenteDaConversa` — o agente aqui é fixo.
  mixins: [historicoConversas],
  data() {
    return {
      personaId: PERSONA_CONSULTOR_ESTRATEGICO,
      // Explícito: sem isto a persona resolveria para `painel` (Multi-agente),
      // que é o primeiro do catálogo a compartilhá-la. A faixa que abre este
      // painel diz "Consultor Estratégico".
      agenteId: AGENTE_CONSULTOR,
      // Separa o custo desta superfície do custo do IA Operacional no relatório
      // por escritório — mesma origem da rota dedicada.
      origemModulo: ORIGEM_SOLICITACOES,
      perguntaPendente: '',
    }
  },
  computed: {
    titulo() {
      return PERSONA.label
    },
    rotuloAba() {
      return ROTULO_ABA
    },
    subtitulo() {
      return this.mostrandoHistorico ? 'Histórico' : ''
    },
    aberto() {
      return this.$store.getters['solicitacoes/consultorPainelAberto']
    },
    // A pergunta do chip. Lida do store e repassada como prop; o motor a envia
    // sozinho por causa de `enviar-pergunta-inicial`.
    perguntaDoChip() {
      return this.$store.getters['solicitacoes/perguntaPendenteConsultor']
    },
  },
  watch: {
    // Consumir aqui, e não no `created`: o painel fica montado enquanto o gestor
    // navega pelo Hub, então uma segunda pergunta chega por mudança de getter, e
    // não por montagem. Sem consumir, um F5 reenviaria a última.
    perguntaDoChip(texto) {
      if (!texto) return
      this.perguntaPendente = texto
      // Perguntar com o histórico aberto tem que trazer a conversa de volta —
      // senão a resposta chega numa tela que o gestor não está vendo.
      this.mostrandoHistorico = false
      this.$store.dispatch('solicitacoes/consumirPerguntaPendenteConsultor')
    },
  },
  created() {
    // Primeira abertura da sessão: a pergunta já está no store quando o painel
    // monta, e o watcher não dispara para o valor inicial.
    const inicial = this.perguntaDoChip
    if (!inicial) return
    this.perguntaPendente = inicial
    this.$store.dispatch('solicitacoes/consumirPerguntaPendenteConsultor')
  },
  methods: {
    abrir() {
      this.$store.dispatch('solicitacoes/abrirPainelConsultor')
    },
    fechar() {
      this.$store.dispatch('solicitacoes/fecharPainelConsultor')
    },
    // Ação própria: o painel do Hub é uma terceira superfície no relatório, e
    // até aqui ele NÃO emitia telemetria nenhuma — cliques em serviço citado
    // pelo Hub não eram medidos.
    onOpenService(payload) {
      abrirServicoCitado(this, payload, {
        module: 'solicitacoes',
        action: 'ia_consultor_painel_abrir_landing',
      })
    },
  },
}
</script>

<template>
  <IaPainelLateral
    :aberto="aberto"
    :titulo="titulo"
    :subtitulo="subtitulo"
    :rotulo-aba="rotuloAba"
    @abrir="abrir"
    @fechar="fechar"
  >
    <template v-slot:acoes>
      <!-- Primeiro slot: "Ver histórico" fora dele, "Voltar" dentro — troca de
           lugar em vez de empilhar um terceiro botão. Ícones, tamanhos e ordem
           copiados de `top-chat-ia-header`: é a mesma conversa noutra
           superfície, e o gestor reconhece os controles pela forma. -->
      <button
        v-if="!mostrandoHistorico"
        type="button"
        class="ia-painel-lateral__botao"
        data-test="alternar-historico"
        aria-label="Ver histórico"
        title="Ver histórico"
        @click="alternarHistorico"
      >
        <icon-svg name="clock-filled" :size="18" color="var(--neutral-700)" />
      </button>

      <button
        v-else
        type="button"
        class="ia-painel-lateral__botao ia-painel-lateral__botao--texto"
        data-test="alternar-historico"
        aria-label="Voltar"
        @click="alternarHistorico"
      >
        <icon-svg name="mdi-arrow-left" :size="14" color="var(--neutral-700)" />
        Voltar
      </button>

      <button
        type="button"
        class="ia-painel-lateral__botao"
        data-test="nova-conversa"
        aria-label="Novo chat"
        title="Novo chat"
        @click="novaConversa"
      >
        <icon-svg name="add-circle" :size="18" color="var(--neutral-700)" />
      </button>
    </template>

    <!-- `v-show`, não `v-if`: alternar para o histórico e voltar não pode
         desmontar o chat — remontá-lo recarregaria a conversa e perderia um
         turno em andamento. -->
    <div v-show="!mostrandoHistorico" class="consultor-painel__chat">
      <!-- `widget`, não `embedded`: o modo widget esconde a sidebar de
           conversas, que não cabe nos 420-640px do painel. Mesmo modo do popup. -->
      <ViewChat
        widget
        iniciar-conversa-nova
        enviar-pergunta-inicial
        :persona="personaId"
        :agente-id="agenteId"
        :origem-modulo="origemModulo"
        :pergunta-inicial="perguntaPendente"
        @open-service="onOpenService"
      />
    </div>

    <div v-if="mostrandoHistorico" class="consultor-painel__historico">
      <!-- Sem `agentes`: o agente é fixo aqui. A troca de agente é do popup,
           que é um chat genérico; este painel é o Consultor e mais nada. -->
      <Conversations
        @select="abrirConversa"
        @new-conversation="novaConversa"
        @delete="apagarConversa"
        @rename="renomearConversa"
      />
    </div>
  </IaPainelLateral>
</template>

<style lang="sass" scoped>
.consultor-painel__chat,
.consultor-painel__historico
  flex: 1 1 auto
  min-height: 0
  display: flex
  flex-direction: column

.consultor-painel__historico
  overflow-y: auto
  padding: 12px
</style>
